import React, { useState } from 'react';
import { View, Picker, Pressable, Switch } from 'react-native';
import { useQuery } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import DataState from 'app/src/components/DataState';
import {
   MainContainer,
   ContentContainer,
   PageTitle,
} from 'app/src/elements';
import { StyledPicker } from 'app/src/elements/inputs';
import Modal from 'app/src/elements/Modal';
import {
  DownloadButton,
  downloadReport,
} from 'app/src/elements/ReportDownload';
import { Text } from 'app/src/styles';
import { useNavigate, useParams, Link } from 'app/src/utils/routing';
import { dollars } from 'app/src/utils/stringHelpers';

import { SALE_ASSET_PRE_BIDS } from './Process/queries';

import PreBidsDetail from './PreBidsDetail';

const Totals = styled(View)`
  flex-direction: row;
  gap: 30;
  margin-left: auto;
  margin-right: auto;
`;

const Total = styled(Text)`
  font-weight: 600;
`;

const Settings = styled(View)`
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: 20px;
  gap: 20px;
  align-items: baseline;
`;

const Reports = styled(View)`
  flex-direction: row;
  gap: 20;
  align-items: baseline;
`;

const columns = [
  {
    key: 'lotNumberSort',
    label: 'Lot #',
    sort: 'prevent',
    value: ({ entry }) => entry.lotNumber,
  },
  {
    key: 'highPreBidPrice',
    label: 'High Bid',
    content: ({ value }) => value ? dollars(value) : '',
  },
  {
    key: 'highPreBid',
    label: 'Bidders Maximum',
    value: ({ value }) => value?.price,
    content: ({ value }) => value ? dollars(value) : '',
  },
  {
    key: 'count',
    label: 'Pre-Bid count',
    value: ({ entry }) => entry.preBids.length,
    content: ({ value, entry }) => {
      if (0 === value) { return null; }

      const [showModal, setShowModal] = useState(false);

      return (
        <View style={{ flexDirection: 'row', gap: 9, alignItems: 'baseline' }}>
          <Text>{value}</Text>
          <Pressable onPress={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </Pressable>

          {showModal && (
            <Modal
              showModal={showModal}
              setShowModal={setShowModal}
              modalType={entry.lotNumber}
            >
              <PreBidsDetail saleAsset={entry} />
            </Modal>
          )}
        </View>
      );
    }
  },
  {
    key: 'buyer',
    label: 'Buyer',
    value: ({ entry }) => entry.highPreBid?.buyerUser?.buyer.name,
    content: ({ value, entry }) => (
      value ? (
        <Link to={`/buyers/${entry.highPreBid.buyerUser.buyer.id}`}>
          <Text>{value}</Text>
        </Link>
      ) : null
    )
  },
  {
    key: 'paddleNumer',
    label: 'Paddle #',
    value: ({ entry }) => entry.highPreBid?.buyerUser?.paddleNumber
  },
  {
    key: 'stockNumber',
    label: 'Stock #',
    value: ({ entry }) => entry.asset.stockNumber,
  },
  {
    key: 'asset',
    label: 'Asset',
    value: ({ value }) => value.name,
  },
];

const PreBids = () => {
  const { saleId } = useParams();
  const [preBidsOnly, setPreBidsOnly] = useState(false);
  const navigate = useNavigate();

  const { data } = useQuery(SALE_ASSET_PRE_BIDS, { variables: { saleId } });

  if (!data) { return <DataState.Loading />; }

  const changeSale = value => {
    if (value) {
      navigate(`/sales/${value}/pre-bids`);
    }
  };

  const sale = data.sales.find(entry => entry.id === saleId) || data.sales[0];

  const saleAssets = preBidsOnly ?
    data.saleAssets.filter(saleAsset => saleAsset.preBids.length > 0) :
    data.saleAssets;

  const handleDownload = downloadProps => {
    downloadReport({
      ...downloadProps,
      path: 'pre-bids-by-lot-report',
      params: {
        'sale-id': saleId,
        'pre-bids-only': preBidsOnly ? 1 : undefined
      }
    })
  };

  const handleDetailDownload = downloadProps => {
    downloadReport({
      ...downloadProps,
      path: 'pre-bids-report',
      params: { 'sale-id': saleId }
    })
  };

  const preBidTotal = saleAssets.reduce((sum, saleAsset) => (
    sum + (saleAsset.highPreBidPrice || 0)
  ), 0);

  const maxBidTotal = saleAssets.reduce((sum, saleAsset) => (
    sum + (saleAsset.highPreBid?.price || 0)
  ), 0);

  const totals = (
    <Totals>
      <Total>Total Pre-Bids: {dollars(preBidTotal)}</Total>
      <Total>Total Bid Maximums: {dollars(maxBidTotal)}</Total>
    </Totals>
  );

  return (
    <MainContainer>
      <ContentContainer>
        <PageTitle>Pre-Bids for {sale.name}</PageTitle>
        <Settings>
          <Text>Show Only Assets with Pre-bids</Text>
          <Switch
            value={preBidsOnly}
            onValueChange={value => setPreBidsOnly(value)}
          />
          <Text>Change Sale:</Text>
          <StyledPicker onValueChange={value => changeSale(value)}>
            <Picker.Item value="" label="Select" />
            {data.sales.toReversed().map(entry => (
              <Picker.Item
                key={entry.id}
                value={entry.id}
                label={entry.name}
              />
            ))}
          </StyledPicker>
        </Settings>

        <Reports>
          <Text>Download Report</Text>
          <DownloadButton handleDownload={handleDownload} />
          <Text>{'  |  '}</Text>
          <Text>Download Details of All Pre-Bids</Text>
          <DownloadButton handleDownload={handleDetailDownload} />
        </Reports>

        <DataTable.Simple
          data={saleAssets}
          columns={columns}
          buttons={totals}
          pageLength="All"
          htmlTable
          sortOnArrowClickOnly
        />
      </ContentContainer>
    </MainContainer>
  );
};

export default PreBids;
